// @ts-nocheck — Phase-14 R2 bulk JSX→TSX rename.
// Phase-16 R2 deliberately leaves @ts-nocheck in place: the file is 530+
// lines of inline JSX and the type sweep is scheduled for R16 (cleanup)
// after the new sections + primitives land. R2 only adds the auth-aware
// redirect + outcome-first headline.
// =====================================================
// Home / Landing page  —  R-Landing-v2 (D47, 2026-05-26)
// =====================================================
// Wrapped in .home-shell-v2 so the design's white+navy palette,
// typography (Vazirmatn + Plus Jakarta), section layouts, and
// reveal-on-scroll animations apply ONLY on / (and never on /login,
// /dashboard, etc.). Per Q1.a: AGENT ARCHITECTURE preserved with
// new palette. Per Q2.b: topbar wrapped INSIDE this scope (sticky
// inside the wrapper, so it physically cannot render on other routes
// since the wrapper isn't in DOM there). NRI rebrand: hero co-brand
// chips = operator mark (پژوهشگاه نیرو) + official-certificate chip;
// all identity strings/assets come from landing/branding.ts.
//
// SW dispose (Commit A): VitePWA disabled + main.tsx top-level
// unregister/clear. This page deploys without any service worker
// caching it = no R-Landing-v1-style precache stickiness.
// =====================================================
import React from "react";
import { Icon } from "../icons";
import { Stagger, useMouseParallax } from "../motion";
import { Footer, toFa, KnowledgeGraph, ArchStack } from "../shared";
import { useAuth } from "../auth/AuthContext";
import { AuthLoadingSkeleton } from "../components/AuthLoadingSkeleton";
import type { Go } from "../router";
import { Button } from "../ui";
// Scoped CSS — every selector is .home-shell-v2-prefixed, so loading
// this file has NO global effect. The CSS only fires inside the
// wrapper div rendered below.
import "./home-v2.css";
// D48 round-2 polish: manual overrides + faculty + testimonials styles
// not covered by the auto-generated home-v2.css.
import "./home-v2-overrides.css";
// S-LP (docs/review/landingpage.md) — the landing redesign sections. All
// branding/stats/copy come from landing/branding.ts (white-label §D.5);
// styles in landing-lp.css are .home-shell-v2-scoped, token-only.
//
// BUNDLE DISCIPLINE: Home is an EAGER route (public LCP path), so every
// static import here lands in the MAIN bundle. The landing sections are all
// below the fold → they load as LAZY chunks (the +72 KiB main-bundle FAIL on
// the first S-LP gate was exactly these static imports). Only the tiny
// branding config stays eager (the hero reads it).
import "./landing-lp.css";
import { BRANDING } from "../landing/branding";

const AiDemoSection = React.lazy(() => import("../landing/AiDemoSection"));
const PricingSection = React.lazy(() => import("../landing/PricingSection"));
const lz = <K extends string>(name: K) =>
  React.lazy(() =>
    import("../landing/LandingSections").then((m) => ({
      default: m[name as keyof typeof m] as React.ComponentType<any>,
    })),
  );
const TrustBar = lz("TrustBar");
const ProblemSolutionSection = lz("ProblemSolutionSection");
const JourneySection = lz("JourneySection");
const ProgramsExplorerSection = lz("ProgramsExplorerSection");
const LiveClassSection = lz("LiveClassSection");
const CredentialVerifySection = lz("CredentialVerifySection");
const MarketplaceSection = lz("MarketplaceSection");
const FaqSection = lz("FaqSection");
const FinalCtaSection = lz("FinalCtaSection");
const StickyMobileCta = lz("StickyMobileCta");

/** Below-the-fold lazy boundary — no fallback flash, sections pop in. */
const L: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={null}>{children}</React.Suspense>
);
// Plus Jakarta Sans deferred — adding it would have required a
// package-lock.json regen + full npm install in the build container.
// The Latin display text in Home falls back to Vazirmatn + system-ui
// sans-serif (declared in home-v2-overrides.css .home-shell-v2 font-family).
// Visually 95% indistinguishable for the demo; can be re-added in a
// future polish with proper lockfile sync.

interface HomePageProps {
  go: Go;
}

export const HomePage = ({ go }: HomePageProps) => {
  // Phase-16 R2 (B-01): a logged-in user landing on "/" is bounced to
  // /dashboard. Without this, the public marketing page rendered on top
  // of the authenticated shell — owner reported "اطلاعات یه یوزر لاگین
  // کرده تو لندینگ هست".
  //
  // Gate-1 Fix 2: render <AuthLoadingSkeleton/> during the one-frame
  // interval between mount and useEffect firing, instead of `return null`
  // (which flashed a blank background). The skeleton matches the nav
  // chrome height so the swap to the real dashboard is layout-silent.
  const auth = useAuth();
  React.useEffect(() => {
    if (auth.isAuthenticated) {
      go("dashboard");
    }
  }, [auth.isAuthenticated, go]);
  // useMouseParallax MUST run unconditionally to satisfy the rules of
  // hooks; the hook itself opts out at runtime when the marketing page
  // isn't mounted (no `.hero-bg .aurora` in the DOM = no work).
  useMouseParallax();

  // R-Landing-v2: reveal-on-scroll IntersectionObserver. Elements with
  // [data-reveal] get .in class when 8% in view. Pulled from the design's
  // app.tsx pattern. Lightweight — no TBT hit at the v1 levels (single
  // observer, no animation clustering).
  React.useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 }
    );
    document.querySelectorAll(".home-shell-v2 [data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // R-Landing-v2: feature card spotlight follows the mouse. Scoped to
  // .home-shell-v2 .feature elements only (won't fire elsewhere).
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(".home-shell-v2 .feature") as HTMLElement | null;
      if (!target) return;
      const r = target.getBoundingClientRect();
      target.style.setProperty("--mx", ((e.clientX - r.left) / r.width * 100) + "%");
      target.style.setProperty("--my", ((e.clientY - r.top) / r.height * 100) + "%");
    };
    document.addEventListener("mousemove", handler);
    return () => document.removeEventListener("mousemove", handler);
  }, []);

  // D48 ITEM 2 — mark body so home-v2-overrides.css can hide the AppShell
  // global Nav while Home is mounted. No :has() involved — React lifecycle
  // controls the attribute. D49 supersedes this with a direct AppShell
  // conditional but the data attribute stays as belt-and-suspenders.
  React.useEffect(() => {
    document.body.dataset.homeShell = "true";
    return () => { delete document.body.dataset.homeShell; };
  }, []);

  // D49 ITEM 7 — mobile hamburger state for the home Nav. Below 1024px
  // the nav-actions row collapses behind a hamburger button; tap opens
  // a drawer from the right edge (RTL start edge). Auto-closes on any
  // nav-link tap so the user can navigate immediately.
  const [homeMenuOpen, setHomeMenuOpen] = React.useState(false);
  // Close on Escape for keyboard a11y.
  React.useEffect(() => {
    if (!homeMenuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setHomeMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [homeMenuOpen]);

  // Phase-A R7.1.1.a — REVERTED the .is-ready gated-animation pattern.
  // Initial attempt clustered 8+ animations into one trigger moment
  // and Lighthouse counted that concentrated work as a +1290ms TBT
  // spike (440 → 1730). Final shape: the static hero IS the design;
  // no JS-driven animation toggle. The auroras and 3D cards paint
  // in their final state and stay there. (Code intentionally absent.)

  if (auth.isAuthenticated) return <AuthLoadingSkeleton />;

  return (
    <div className="home-shell-v2">
      {/* ============== TOPBAR (Q2.b — institutional badge) ============== */}
      {/* Scoped INSIDE .home-shell-v2 so it cannot render on any other
          route. position: sticky from home-v2.css makes it stick within
          this wrapper. Identity strings from BRANDING (white-label). */}
      <div className="topbar">
        <div className="container topbar-inner">
          <div className="left">
            <span className="badge">
              <Icon name="star" size={14} />
              زیر نظر <b style={{ color: "white", fontWeight: 600, margin: "0 4px" }}>{BRANDING.name}</b> — {BRANDING.parentOrg.name}
            </span>
            <span className="sep" />
            <span className="badge">
              <Icon name="check" size={13} />
              {BRANDING.trustBar[1]}
            </span>
          </div>
          <div className="right">
            <a href={"mailto:" + BRANDING.contact.email}>
              <Icon name="globe" size={12} />
              {BRANDING.contact.email}
            </a>
            <span className="sep" />
            <span>
              <Icon name="chat" size={12} />
              پشتیبانی ۲۴ / ۷
            </span>
          </div>
        </div>
      </div>

      {/* ============== HOME NAV (D48 ITEM 2 — replaces AppShell Nav on /) ============== */}
      {/* AppShell Nav is hidden via body[data-home-shell] data-attribute set
          by the useEffect above. This local nav uses the design's logo +
          brand text and routes to the same public destinations. */}
      <nav className="home-nav-v2" aria-label="ناوبری اصلی صفحه خانه">
        <div className="home-nav-inner">
          <div className="home-brand">
            {/* NRI rebrand — operator mark (local webp, 192px intrinsic for a
                44px slot) + platform/operator names from BRANDING. */}
            <img src={BRANDING.logos.mark} alt={BRANDING.name} width="44" height="44" />
            <div className="home-brand-text">
              <span className="home-brand-name">{BRANDING.platformName}</span>
              <span className="home-brand-sub">{BRANDING.name}</span>
            </div>
          </div>
          {/* D49 ITEM 7 — mobile hamburger. CSS hides this above 1024px
              and hides the inline .home-nav-actions below 1024px. */}
          <button
            type="button"
            className="home-nav-burger"
            aria-label={homeMenuOpen ? "بستن منو" : "باز کردن منو"}
            aria-expanded={homeMenuOpen}
            aria-controls="home-nav-drawer"
            onClick={() => setHomeMenuOpen((v) => !v)}
          >
            <span className="burger-line"></span>
            <span className="burger-line"></span>
            <span className="burger-line"></span>
          </button>
          <div className="home-nav-actions">
            <button type="button" className="home-nav-link" onClick={() => go("about")}>
              درباره‌ی ما
            </button>
            <button type="button" className="home-nav-link" onClick={() => go("programs")}>
              دوره‌ها و مسیرها
            </button>
            <button type="button" className="home-nav-link" onClick={() => go("login")}>
              ورود
            </button>
            <button type="button" className="home-nav-link is-primary" onClick={() => go("apply")}>
              {BRANDING.copy.primaryCta}
            </button>
          </div>
        </div>
        {/* Mobile drawer — collapsed at ≥1024 via CSS. */}
        {homeMenuOpen ? (
          <>
            <div
              className="home-nav-backdrop"
              onClick={() => setHomeMenuOpen(false)}
              aria-hidden="true"
            />
            <div id="home-nav-drawer" className="home-nav-drawer" role="dialog" aria-label="منوی ناوبری">
              <button
                type="button"
                className="home-nav-link"
                onClick={() => { setHomeMenuOpen(false); go("about"); }}
              >
                درباره‌ی ما
              </button>
              <button
                type="button"
                className="home-nav-link"
                onClick={() => { setHomeMenuOpen(false); go("programs"); }}
              >
                دوره‌ها و مسیرها
              </button>
              <button
                type="button"
                className="home-nav-link"
                onClick={() => { setHomeMenuOpen(false); go("login"); }}
              >
                ورود
              </button>
              <button
                type="button"
                className="home-nav-link is-primary"
                onClick={() => { setHomeMenuOpen(false); go("apply"); }}
              >
                {BRANDING.copy.primaryCta}
              </button>
            </div>
          </>
        ) : null}
      </nav>

      <main data-screen-label="01 خانه">
      {/* ============== HERO ============== */}
      <section className="hero">
        <div className="hero-bg">
          <div className="aurora aurora-1"></div>
          <div className="aurora aurora-2"></div>
          <div className="aurora aurora-3"></div>
        </div>

        <div className="shell hero-stage">
          <div className="hero-headline">
            {/* NRI rebrand co-brand chips: operator mark (پژوهشگاه نیرو) from
                BRANDING + an official-certificate chip (Icon, no fake logo).
                The وزارت نیرو parent mark renders exactly once, in the final
                CTA (LandingSections.FinalCtaSection) — not here. */}
            <div className="hero-crown" data-reveal>
              <div className="logo-card">
                <div className="l-img">
                  <img src={BRANDING.logos.mark} alt={BRANDING.name} width="192" height="192" />
                </div>
                <div className="l-text">
                  <b>{BRANDING.name}</b>
                  <small>مجری سامانه — زیر نظر {BRANDING.parentOrg.name}</small>
                </div>
              </div>
              <div className="logo-card">
                <div className="l-img" style={{ color: "#fff" }}>
                  <Icon name="cert" size={34} />
                </div>
                <div className="l-text">
                  <b>گواهی رسمی نوع اول و دوم</b>
                  <small>یکپارچه با سامانهٔ صلاحیت حرفه‌ای (tvqc)</small>
                </div>
              </div>
            </div>
            {/* NRI rebrand — the large centered hero lockup is the FULL
                پژوهشگاه نیرو logo from the branding config (BRANDING.logos.full,
                local asset, no CDN). fetchpriority=high + decoding=sync keep
                the LCP prioritization of the previous lockup. */}
            <div className="hero-logo-mark" data-reveal>
              <img
                src={BRANDING.logos.full}
                alt={BRANDING.platformName + " — " + BRANDING.name}
                fetchPriority="high"
                decoding="sync"
                width="701"
                height="920"
              />
            </div>
            {/* S-LP-2 (landingpage.md §D.1 بخش ۱) — benefit-first H1 + ONE
                primary CTA (free-plan framing, no card) + a secondary CTA that
                scrolls to the AI demo. Copy comes from the branding config
                (white-label). The D58 logo lockup above is UNTOUCHED. */}
            <h1 className="hero-title">
              <span className="hero-title-line">{BRANDING.copy.heroTitle}</span>
            </h1>
            <p className="hero-sub">{BRANDING.copy.heroSub}</p>
            <div className="hero-cta">
              <Button variant="primary" size="lg" onClick={() => go("apply")}>
                {BRANDING.copy.primaryCta}
                <Icon name="arrow" size={16} />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() =>
                  document.getElementById("lp-ai-demo")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                <Icon name="sparkle" size={16} />
                {BRANDING.copy.secondaryCta}
              </Button>
            </div>
          </div>

          {/* 3D layered preview */}
          <div className="hero-3d">
            <Hero3DClassroom />
            <Hero3DTutor />
            <Hero3DAnalytics />
            <Hero3DCredential />
          </div>

          {/* S-LP-2 — the credibility strip reads its numbers from the
              branding config (white-label §D.5), Persian digits via toFa. */}
          <div className="hero-stats">
            <Stat v={toFa(BRANDING.stats.faculties)} unit=" هلدینگ" l="توانیر · برق حرارتی · منابع آب · آبفا · ساتکاب" />
            <Stat v={toFa(BRANDING.stats.programs)} unit=" شرکت زیرمجموعه" l="پوشش سراسری صنعت آب و برق" />
            <Stat v={toFa(BRANDING.stats.instructors)} unit=" نوع گواهی رسمی" l="نوع اول و نوع دوم — ساعت‌محور" />
            <Stat v={toFa(BRANDING.stats.students.toLocaleString("en-US"))} unit=" کارمند صنعت" l="دامنهٔ پوشش هدف — طرح ملی" />
          </div>
        </div>
      </section>

      {/* ============== TRUST STRIP (R9 §2) — REMOVED per D54 owner directive.
           Owner explicit «remove this section in landing page» pointing at the
           old partners strip. The structured D53 <PartnersV2Section /> below
           (rendered further down the page) provides the ecosystem affiliations
           with the visual treatment owner approved. TrustStrip definition is
           preserved further down for potential reuse but not rendered. */}

      {/* ============== S-LP بخش ۲ — نوار اعتماد رسمی ============== */}
      <L><TrustBar /></L>

      {/* ============== STATS BAND (R9 §3) ============== */}
      <StatsBand />

      {/* ============== S-LP بخش ۳ — مشکل ← راه‌حل ============== */}
      <L><ProblemSolutionSection /></L>

      {/* ============== S-LP بخش ۴ ⭐ — دموی تعاملی دستیارها ============== */}
      <L><AiDemoSection go={go} /></L>

      {/* ============== AGENT SYSTEM ============== */}
      <section className="section">
        <div className="shell">
          <div className="section-head reveal">
            <div className="text">
              <span className="eyebrow">AGENT ARCHITECTURE</span>
              <h2 className="h-1">
                به‌جای یک چت‌بات،
                <br />
                <span style={{ color: "var(--violet)" }}>یک تیم آموزشی</span>
              </h2>
            </div>
            <p className="lead">
              هر دوره یک Agent Graph مستقل دارد. پنج عامل تخصصی، هر یک با نقش، مرز، منابع مجاز و معیار ارزیابی خود. مدرس بر همه چیز نظارت دارد.
            </p>
          </div>

          <div className="pillar-grid stagger">
            {/* Pillar 1 — agent graph */}
            <div className="pillar pillar-1 reveal">
              <div className="glow"></div>
              <span className="num">۰۱ / موتور یادگیری</span>
              <h3>پنج عامل، یک هدف: تسلط واقعی</h3>
              <p>توضیح‌دهنده، مربی پرسش‌گر، منتقد، منتور بلندمدت و ارزیاب — هر کدام در حوزه‌ی تخصص خود و تحت کنترل مدرس.</p>
              <div className="agent-graph">
                {[
                  ["TUTOR", "توضیح‌دهنده"],
                  ["COACH", "مربی"],
                  ["CRITIC", "منتقد"],
                  ["MENTOR", "منتور"],
                  ["GRADER", "ارزیاب"],
                  ["HUMAN", "مدرس"],
                ].map(([k, l]) => (
                  <div key={k} className="agent-node">
                    <div className="nm">{k}</div>
                    <div className="lb">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pillar pillar-2 reveal">
              <div className="glow"></div>
              <span className="num">۰۲ / پروفایل شناختی</span>
              <h3>هر فراگیر، یک نقشه‌ی زنده</h3>
              <p>دانش قبلی، سرعت یادگیری، مفاهیم مسلط و مبهم، سبک تعامل و ریسک افت — همگی به‌روزرسانی لحظه‌ای.</p>
              <div className="pt-5 flex gap-2.5 flex-wrap"  style={{marginTop: "auto"}}>
                <span className="pill pill-cyan">Bayesian KT</span>
                <span className="pill pill-amber">Deep KT</span>
                <span className="pill">IRT 2PL</span>
              </div>
            </div>

            <div className="pillar pillar-3 reveal">
              <div className="glow"></div>
              <span className="num">۰۳ / تسلط</span>
              <h3>عبور با شواهد، نه با حضور</h3>
              <p>هر فعالیت به یک هدف یادگیری متصل است. آستانه‌ی تسلط قابل تنظیم. سامانه می‌داند کجا تمرین بیشتر نیاز است.</p>
              <div className="pt-5"  style={{marginTop: "auto"}}>
                <MasteryRing percent={78} />
              </div>
            </div>

            <div className="pillar pillar-4 reveal">
              <div className="glow"></div>
              <span className="num">۰۴ / کلاس زنده</span>
              <h3>WebRTC · LL-HLS · ضبط هوشمند</h3>
              <p>کلاس کوچک تعاملی تا وبینار هزاران‌نفره. ضبط، گفتار به متن، تشخیص گوینده، فصل‌بندی خودکار — همه استاندارد.</p>
            </div>

            <div className="pillar pillar-5 reveal">
              <div className="glow"></div>
              <span className="num">۰۵ / حاکمیت AI</span>
              <h3>Human-in-the-loop در نقاط حساس</h3>
              <p>هیچ نمره‌ای بدون امکان بازبینی انسانی نهایی نمی‌شود. هر پاسخ AI با منبع و امضای عامل.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== S-LP بخش ۵ — مسیر سفر ۵ قدمی ============== */}
      <L><JourneySection /></L>

      {/* ============== S-LP بخش ۶ — برنامه‌ها با فیلتر مقطع (دادهٔ واقعی) ============== */}
      <L><ProgramsExplorerSection go={go} /></L>

      {/* ============== CATALOG TEASER (R10 §5) ============== */}
      <CatalogTeaser go={go} />

      {/* ============== FACULTY SHOWCASE (R10 §4) ============== */}
      {/* D48 ITEM 4 — Faculty section per design (8 portraits + data verbatim). */}
      <FacultyV2Section />

      {/* ============== S-LP بخش ۷ — کلاس زندهٔ خودمیزبان ============== */}
      <L><LiveClassSection go={go} /></L>

      {/* ============== S-LP بخش ۸ — پلن‌ها (دادهٔ واقعی /pricing) ============== */}
      <L><PricingSection go={go} /></L>

      {/* ============== S-LP بخش ۹ — راستی‌آزمایی گواهی ============== */}
      <L><CredentialVerifySection go={go} /></L>

      {/* ============== S-LP بخش ۱۰ — بازارگاه دانش / مدرس شو ============== */}
      <L><MarketplaceSection go={go} /></L>

      {/* ============== TESTIMONIALS (R10 §6) — اثبات اجتماعی ============== */}
      <TestiV2Section />

      {/* ============== PARTNERS V2 (D53) — owner: «جذاب نیست درستش کن».
           Replaces the plain scrolling-text marquee with a structured
           card-grid section organized into 3 categories. Each card shows
           a stylized mark (initials in colored circle) + full name +
           sublabel. Hover lift + accent border. */}
      <PartnersV2Section />


      {/* ============== KNOWLEDGE GRAPH ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="grid grid-2 gap-12 items-center"  style={{gridTemplateColumns: "1fr 1.1fr"}}>
            <div>
              <span className="eyebrow">KNOWLEDGE GRAPH</span>
              <h2 className="h-1 mt-4" >
                هر دوره یک
                <br />
                <span style={{ color: "var(--cyan)" }}>شبکه‌ی مفهومی</span> است
              </h2>
              <p className="lead mt-5" >
                وقتی فراگیر روی یک مفهوم گیر می‌کند، سامانه می‌داند کدام پیش‌نیازها را مرور کند، کدام تمرین جبرانی بدهد و کدام بخش از ویدئوی کلاس را نشان دهد.
              </p>
              <div className="grid mt-8 gap-4.5"  style={{gridTemplateColumns: "1fr 1fr"}}>
                <Feature title="بازیابی هیبرید" desc="BM25 + dense embeddings برای جستجوی معنایی" icon="search" />
                <Feature title="جهش به ثانیه" desc="نتایج جستجو مستقیماً به ثانیه‌ی دقیق ویدئو" icon="play" />
                <Feature title="استخراج مفهوم" desc="NER و تحلیل ابهام در لحظه‌ی تدریس" icon="sparkle" />
                <Feature title="مسیر جبرانی" desc="پیشنهاد خودکار بر اساس پروفایل شناختی" icon="target" />
              </div>
            </div>

            <div className="kg-wrap">
              <KnowledgeGraph />
            </div>
          </div>
        </div>
      </section>

      {/* ============== ARCHITECTURE ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="grid grid-2 gap-12 items-center"  style={{gridTemplateColumns: "1.1fr 1fr"}}>
            <div className="kg-wrap grid"  style={{minHeight: 480, placeItems: "center"}}>
              <ArchStack />
            </div>
            <div>
              <span className="eyebrow">PLATFORM ARCHITECTURE</span>
              <h2 className="h-1 mt-4" >
                معماری <span style={{ color: "var(--amber)" }}>پنج لایه</span>،
                <br />
                cloud-native و قابل خودمیزبانی
              </h2>
              <p className="lead mt-5" >
                از SaaS برای مؤسسات کوچک تا Private Cloud و On-Premise برای نهادهای حساس. هر سرویس مستقل، هر سرویس قابل جایگزینی.
              </p>
              <div className="mt-7" >
                <TechRow label="Frontend" items={["Next.js 15", "TypeScript", "Tailwind", "WebRTC client"]} />
                <TechRow label="Backend" items={["NestJS", "Spring Boot", "FastAPI", "gRPC"]} />
                <TechRow label="AI & Data" items={["LangGraph", "Qdrant", "Whisper", "MLflow"]} />
                <TechRow label="Infra" items={["Kubernetes", "Kafka", "ClickHouse", "ArgoCD"]} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== COURSES ============== */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <div className="text">
              <span className="eyebrow">CATALOG</span>
              <h2 className="h-1 mt-4" >دوره‌هایی که با نقش شغلی تو جلو می‌روند</h2>
            </div>
            <Button variant="outline" onClick={() => go("programs")}>
              همه‌ی دوره‌ها
              <Icon name="arrow" size={14} />
            </Button>
          </div>

          <Stagger className="grid grid-3">
            {COURSES.map((c) => (
              <button
                key={c.title}
                type="button"
                className="course-card reveal"
                onClick={() => go("course")}
                aria-label={`دورهٔ ${c.title} — ${c.by}`}
              >
                <div className={"course-cover " + c.cover}>
                  <span className="glyph">{c.glyph}</span>
                  <span className="pill mono">{c.code}</span>
                </div>
                <div className="course-body">
                  {/* R7.3 A.3.i — was `<h4>` directly under section's
                      `<h2>` ("دروسی که با تو رشد می‌کنند") which skips
                      h3 (heading-order fail). Renamed to `<h3>`;
                      `.course-body h4` rule in styles.css updated to
                      `.course-body h3` to keep the visual treatment. */}
                  <h3>{c.title}</h3>
                  <div className="by">با {c.by}</div>
                  <p style={{ fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.6 }}>{c.desc}</p>
                  <div className="course-meta">
                    <span className="it"><strong>{c.weeks}</strong> هفته</span>
                    <span className="it"><strong>{c.modules}</strong> ماژول</span>
                    <span className="it"><strong>{c.hours}</strong> ساعت گواهی</span>
                  </div>
                </div>
              </button>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============== STANDARDS ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="card p-12 text-center" >
            <span className="eyebrow justify-center" >STANDARDS · INTEROPERABILITY</span>
            <h2 className="h-2 mt-4.5"  style={{ maxWidth: 720, margin: "18px auto 0"}}>
              ساخته‌شده بر شانه‌ی استانداردهای جهانی یادگیری الکترونیکی سازمانی
            </h2>
            <div className="standards justify-center mt-8" >
              <span className="std">LTI 1.3 / Advantage</span>
              <span className="std">xAPI · cmi5</span>
              <span className="std">QTI 3.0</span>
              <span className="std">Caliper Analytics</span>
              <span className="std">OneRoster 1.2</span>
              <span className="std">Open Badges 3.0</span>
              <span className="std">Verifiable Credentials</span>
              <span className="std">WCAG 2.2 AA</span>
              <span className="std">SCORM legacy</span>
              <span className="std">SAML 2.0 / OIDC</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PLATFORM TOUR ============== */}
      <section className="section pt-0" >
        <div className="shell">
          <div className="section-head">
            <div className="text">
              <span className="eyebrow">PLATFORM TOUR · ۲۰ SCREEN</span>
              <h2 className="h-1 mt-4" >هر نقش، یک محیط کاری مستقل</h2>
            </div>
            <p className="lead">
              فراگیر، مدرس، رابط آموزش شرکت، مدیر — هر یک ابزار خود را دارد. همه روی یک سکو.
            </p>
          </div>

          <Stagger className="grid grid-4">
            {[
              { id: "classroom", t: "کلاس زنده", d: "WebRTC خودمیزبان + زیرنویس فارسی", ic: "live" },
              { id: "labs", t: "آزمایشگاه مجازی", d: "هیدرولیک و هیدرولوژی آب · پخش بار و حفاظت برق", ic: "flask" },
              { id: "schools", t: "هلدینگ‌ها و شرکت‌ها", d: "۵ هلدینگ · ۱۸۰ شرکت زیرمجموعه", ic: "grad" },
              { id: "research", t: "میز دانش فنی", d: "گزارش‌ها، مراجع فنی، گروه‌های تخصصی", ic: "flask" },
              { id: "dashboard", t: "میز کار فراگیر", d: "شناسنامهٔ آموزشی · سقف ساعت سالانه", ic: "home" },
              { id: "course", t: "صفحه دوره", d: "نقشه ماژول، دستیار هوشمند جانبی", ic: "book" },
              { id: "search", t: "جستجوی معنایی", d: "BM25 + dense + RAG", ic: "search" },
              { id: "calendar", t: "تقویم آموزشی", d: "گروه‌های دوره · نامزدسازی شرکت", ic: "calendar" },
              { id: "library", t: "کتابخانه دیجیتال", d: "منابع فنی صنعت، جستجوی معنایی", ic: "folder" },
              { id: "assessment", t: "آزمون و ارزشیابی", d: "۱۴ نوع سؤال + نظارت انسانی", ic: "check" },
              { id: "community", t: "جامعه‌ی یادگیری", d: "انجمن تخصصی صنعت + خوشه‌بندی AI", ic: "users" },
              { id: "recordings", t: "آرشیو کلاس‌ها", d: "ویدئو + متن + کوییز خودکار", ic: "video" },
              { id: "instructor", t: "کنسول مدرس", d: "سلامت کلاس + بازبینی AI", ic: "chip" },
              { id: "authoring", t: "استودیو دانش", d: "تولید پکیج آموزشی + AI Planner", ic: "sparkle" },
              { id: "analytics", t: "تحلیل‌گری سازمانی", d: "Cohort, heatmap, early warning", ic: "chart" },
              { id: "admin", t: "میز مدیریت", d: "KPI، حاکمیت AI، کاربران", ic: "settings" },
              { id: "parent", t: "پورتال رابط آموزش", d: "پایش پیشرفت کارکنان شرکت", ic: "users" },
              { id: "officehours", t: "Office Hours", d: "رزرو جلسه با مدرس", ic: "calendar" },
              { id: "events", t: "رویدادها و وبینارها", d: "کنفرانس، کارگاه، سخنرانی مهمان", ic: "live" },
              { id: "faculty", t: "مدرسان و خبرگان", d: "مدرسان پژوهشگاه و خبرگان صنعت", ic: "grad" },
              { id: "help", t: "مرکز پشتیبانی", d: "راهنماها، FAQ، چت ۲۴/۷", ic: "headset" },
              { id: "pricing", t: "مدل پرداخت", d: "پرداخت سازمانی · پکیج اختیاری", ic: "dollar" },
              { id: "credential", t: "گواهی دیجیتال", d: "نوع اول/دوم · قابل‌راستی‌آزمایی", ic: "cert" },
              { id: "about", t: "درباره ما", d: "ماموریت، اصول، تیم", ic: "sparkle" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                className="reveal rounded-xl p-5 cursor-pointer relative overflow-hidden text-right"
                onClick={() => go(p.id)}
                aria-label={`${p.t} · ${p.d}`}
                style={{background: "var(--surface)",
                border: "1px solid var(--line)",
                transition: "200ms ease",
                fontFamily: "inherit",
                color: "var(--fg)"}}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--accent)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div className="flex items-center justify-between mb-3.5" >
                  <div className="rounded-lg grid"  style={{width: 36, height: 36, background: "var(--surface-2)", color: "var(--accent)", placeItems: "center"}}>
                    <Icon name={p.ic} size={16} />
                  </div>
                  <Icon name="arrow" size={14} stroke={2} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{p.t}</div>
                <div className="mt-1.5"  style={{fontSize: 12, color: "var(--fg-mute)", lineHeight: 1.5}}>{p.d}</div>
              </button>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============== S-LP بخش ۱۲ — سؤالات متداول + FAQPage schema ============== */}
      <L><FaqSection /></L>

      {/* ============== CTA (نهادی — حفظ‌شده) ============== */}
      <section className="section">
        <div className="shell">
          <div className="card grid gap-12 items-center"  style={{padding: "64px 48px",
            background: "linear-gradient(135deg, var(--surface) 0%, color-mix(in oklch, var(--cyan) 8%, var(--surface-2)) 100%)", gridTemplateColumns: "1.4fr 1fr"}}>
            <div>
              <span className="eyebrow">GET STARTED</span>
              <h2 className="h-1 mt-4" >آموزش ضمن خدمت شرکت‌تان را هوشمند کنید</h2>
              <p className="lead mt-4" >
                در کمتر از ۳۰ روز، آموزش ضمن خدمت شرکت خود را با کلاس زنده، ضبط هوشمند، آزمون تطبیقی و گواهی رسمی ساعت‌محور راه‌اندازی کنید.
              </p>
              <div className="flex gap-3 mt-8" >
                <Button variant="primary" size="lg" onClick={() => go("apply")}>
                  درخواست دموی سازمانی
                  <Icon name="arrow" size={16} />
                </Button>
                <Button variant="outline" size="lg">
                  <Icon name="download" size={16} />
                  دانلود پروپوزال
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2.5" >
              {[
                ["راه‌اندازی در ۳۰ روز", "اولین گروه آموزشی شرکت فعال"],
                ["تقویم کامل در ۹۰ روز", "تمام سرویس‌های آموزش سازمان آماده"],
                ["مقیاس ملی در ۶ ماه", "چند مستاجره، SLA 99.9٪"],
              ].map(([t, s], i) => (
                <div key={i} className="card-flat flex items-center gap-3.5" >
                  <div className="rounded-xl grid"  style={{width: 36, height: 36, background: "var(--surface-3)", color: "var(--cyan)", placeItems: "center", fontFamily: "var(--f-mono)", fontWeight: 700, fontSize: 13}}>
                    {toFa(i + 1)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t}</div>
                    <div className="mt-0.5"  style={{color: "var(--fg-mute)", fontSize: 12}}>{s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== S-LP بخش ۱۳ — CTA نهایی ============== */}
      <L><FinalCtaSection go={go} /></L>
      </main>

      {/* S-LP — CTA چسبان موبایل (فقط <768px، تپ‌تارگت ≥۴۴px) */}
      <L><StickyMobileCta go={go} /></L>
    </div>
  );
};

// ====== sub-components for home ======

// D48 ITEM 4 — Faculty section per Claude Design data.tsx verbatim.
// 8 portraits served from /landing-v2/faculty/. Data preserves design's
// exact names, roles, and fields. Layout: 4-col desktop / 2-col tablet
// / 1-col mobile (CSS in home-v2-overrides.css .faculty-v2-grid).
// R7.1.5.b — portrait PNGs converted to optimized JPGs via sharp
// (`scripts/optimize-landing-images.mjs` author-time one-off).
// Total portrait weight: 11.4 MB → ~250 KB (-97%). Visual quality
// preserved at 600w + JPG quality 82 + mozjpeg encoder. See decision
// log D57 for the rationale.
// NRI rebrand — نمونهٔ حوزه‌های تدریس (اسامی نمایشی؛ بدون ادعای وابستگی به
// نهاد واقعی — صداقت بلوک ۵.۳). Roles = حوزه‌های صنعت آب و برق، نه رتبهٔ آکادمیک.
const FACULTY_V2 = [
  { name: "دکتر سید محمد حسینی", role: "مدرس ارشد حوزهٔ برق",           field: "حفاظت و کنترل شبکهٔ قدرت",          photo: "/landing-v2/faculty/m2.webp" },
  { name: "دکتر زهرا کریمی",     role: "مدرس حوزهٔ آب",                  field: "هیدرولوژی و مدیریت منابع آب",       photo: "/landing-v2/faculty/w1.webp" },
  { name: "مهندس علیرضا مظفری",  role: "خبرهٔ صنعت برق",                 field: "بهره‌برداری شبکهٔ انتقال و توزیع",   photo: "/landing-v2/faculty/m1.webp" },
  { name: "دکتر مریم باقری",     role: "مدرس مدیریت صنعتی",              field: "مدیریت دارایی‌ها و نگهداشت",        photo: "/landing-v2/faculty/w2.webp" },
  { name: "مهندس امیر طاهری",    role: "خبرهٔ نیروگاهی",                 field: "بهره‌برداری نیروگاه حرارتی",         photo: "/landing-v2/faculty/m3.webp" },
  { name: "دکتر نسرین مرادی",    role: "مدرس HSE",                      field: "ایمنی و محیط‌زیست تأسیسات",          photo: "/landing-v2/faculty/w3.webp" },
  { name: "دکتر کامران اسدی",    role: "مدرس هوش مصنوعی",               field: "تحلیل داده در صنعت انرژی",           photo: "/landing-v2/faculty/m4.webp" },
  { name: "مهندس فاطمه نصیری",   role: "خبرهٔ آب و فاضلاب",              field: "فرایندهای تصفیه و بهره‌برداری آبفا",  photo: "/landing-v2/faculty/w4.webp" },
];

const FacultyV2Section = () => (
  <section className="faculty-v2-section" data-reveal>
    <div className="shell">
      <div className="section-head">
        <div>
          <div className="section-eyebrow">مدرسان و خبرگان صنعت</div>
          <h2 className="section-title">
            تجربه‌ی <span className="em">صنعت آب و برق</span> در کنار شما.
          </h2>
        </div>
        <p className="section-lead">
          دوره‌ها را مدرسان پژوهشگاه نیرو در کنار خبرگان شاغل صنعت آب و برق طراحی و تدریس می‌کنند — از نیروگاه و شبکهٔ انتقال تا تصفیه‌خانه و شبکهٔ توزیع آب. نمونهٔ حوزه‌های تدریس:
        </p>
      </div>
      <div className="faculty-v2-grid">
        {FACULTY_V2.map((f) => (
          <article key={f.name} className="faculty-v2-card">
            <div className="portrait">
              {/* R7.1.5.a — explicit decoding=async + fetchpriority=low
                  so lazy faculty PNGs (1–2 MB each) don't compete with
                  hero LCP candidate for main thread time. */}
              <img
                src={f.photo}
                alt={f.name}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width="320"
                height="427"
              />
            </div>
            <div className="info">
              <div className="name">{f.name}</div>
              <div className="role">{f.role}</div>
              <div className="field">{f.field}</div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Stat = ({ v, unit, l }) => (
  <div className="hero-stat">
    <div className="v">{v}{unit && <span className="unit">{unit}</span>}</div>
    <div className="l">{l}</div>
  </div>
);

// D48 ITEM 5 — Testimonials per Claude Design data.tsx verbatim.
// 3 student stories with circular avatar (photo reused from faculty
// portraits per design's pattern — the design also reuses these as
// generic student avatars). Initials are the text fallback if photo
// fails to load. Layout: 3-col desktop / 2-col tablet (≤1024) / 1-col
// mobile (≤640) via .testi-v2-grid in home-v2-overrides.css.
// D51 ITEM C — testimonials avatars upgraded. Removed faculty-photo reuse
// (owner reported the faculty headshots looked off as "student" avatars).
// Replaced with stylized initial-letter avatars on gradient circles +
// inline graduation-cap SVG decoration in each card. Professional,
// student-relevant, no external asset dependency.
// NRI rebrand (صداقت بلوک ۵.۳ — بدون testimonial جعلی): the cards are now
// EXPLICITLY-LABELLED sample usage scenarios (سناریوی نمایشی) for the three
// core personas of the in-service world — نه نقل‌قول واقعی از اشخاص. Photos
// dropped (initial-letter avatars only) so no real face implies a real user.
const TESTI_V2 = [
  {
    body: "صبح، فهرست دوره‌های الزامی نقشم را می‌بینم؛ ظهر در کلاس زندهٔ حفاظت شبکه شرکت می‌کنم و عصر همان سناریو را در شبیه‌ساز پخش بار تمرین می‌کنم — ساعت‌های آموزشی خودکار در شناسنامهٔ آموزشی‌ام ثبت می‌شود.",
    name: "کارشناس بهره‌برداری شبکهٔ برق",
    role: "سناریوی نمایشی · هلدینگ توانیر",
    initials: "ب",
    accent: "navy",
    photo: null,
  },
  {
    body: "به‌عنوان رابط آموزش شرکت، کارکنان را برای گروه‌های تقویم آموزشی نامزد می‌کنم، سقف ساعت سالانهٔ هر نفر را پایش می‌کنم و پرداخت دوره‌های الزامی از کیف پول سازمانی انجام می‌شود.",
    name: "رابط آموزش شرکت آب و فاضلاب",
    role: "سناریوی نمایشی · هلدینگ آبفا",
    initials: "ر",
    accent: "cobalt",
    photo: null,
  },
  {
    body: "برای ارتقای رتبهٔ شغلی، مسیر شایستگی‌ام را دنبال می‌کنم؛ پکیج اختیاری هیدرولوژی را از بازار گرفتم و کارفرما گواهی نوع دومم را با لینک راستی‌آزمایی، آنلاین تأیید کرد.",
    name: "کارشناس منابع آب",
    role: "سناریوی نمایشی · مدیریت منابع آب ایران",
    initials: "م",
    accent: "gold",
    photo: null,
  },
];

// Small inline grad-cap SVG used as a decorative element in each testimonial
// card. Single-color, currentColor-aware, ~28px square.
const GradCapSvg = () => (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 4 10 5-10 5L2 9z" />
    <path d="M6 11v4a6 6 0 0 0 12 0v-4" />
    <path d="M21 10v6" />
  </svg>
);

// NRI rebrand — the 3-category partner grid now shows the REAL ecosystem of
// the platform (بلوک ۵.۳ صداقت): the 5 وزارت نیرو holdings the platform
// serves, the governing bodies, and the legal/certification frameworks.
// Structure (grid + monogram card + accent) unchanged.
type PartnerEntry = { mark: string; name: string; sub: string; accent: "navy" | "cobalt" | "gold" };

const PARTNERS_HOLDINGS: PartnerEntry[] = [
  { mark: "ت", name: "توانیر", sub: "انتقال و توزیع برق", accent: "navy" },
  { mark: "ح", name: "تولید نیروی برق حرارتی", sub: "نیروگاه‌های حرارتی", accent: "navy" },
  { mark: "آ", name: "مدیریت منابع آب ایران", sub: "منابع آب و سدها", accent: "navy" },
  { mark: "ف", name: "آبفا", sub: "آب و فاضلاب کشور", accent: "navy" },
  { mark: "س", name: "ساتکاب", sub: "تأمین تجهیزات و پشتیبانی", accent: "navy" },
];

const PARTNERS_GOVERNANCE: PartnerEntry[] = [
  { mark: "و", name: "وزارت نیرو", sub: "نهاد بالادست", accent: "cobalt" },
  { mark: "پ", name: "پژوهشگاه نیرو", sub: "مجری سامانه", accent: "cobalt" },
  { mark: "ص", name: "سامانهٔ مرکزی صلاحیت حرفه‌ای", sub: "tvqc · صدور و راستی‌آزمایی گواهی", accent: "cobalt" },
];

const PARTNERS_FRAMEWORKS: PartnerEntry[] = [
  { mark: "ن", name: "نظام آموزش کارمندان دولت", sub: "چارچوب آموزش ضمن خدمت", accent: "gold" },
  { mark: "م", name: "مادهٔ ۵۸ قانون مدیریت خدمات کشوری", sub: "مبنای قانونی آموزش کارکنان", accent: "gold" },
  { mark: "گ", name: "گواهی نوع اول و دوم", sub: "ساعت‌محور — مدرک تحصیلی نیست", accent: "gold" },
];

const PartnersV2Section = () => (
  <section className="partners-v2-section" data-reveal>
    <div className="shell">
      <div className="section-head">
        <div>
          <div className="section-eyebrow">اکوسیستم صنعت آب و برق</div>
          <h2 className="section-title">
            در <span className="em">شبکهٔ رسمی صنعت</span> آب و برق کشور
          </h2>
        </div>
        <p className="section-lead">
          سامانه برای کارکنان هلدینگ‌ها و شرکت‌های زیرمجموعهٔ وزارت نیرو طراحی شده و اعتبار
          گواهی‌ها از مبنای قانونی و یکپارچگی با سامانهٔ صلاحیت حرفه‌ای می‌آید.
        </p>
      </div>

      <div className="partners-v2-group">
        <h3 className="partners-v2-group-title">هلدینگ‌های تحت پوشش</h3>
        <div className="partners-v2-grid">
          {PARTNERS_HOLDINGS.map((p) => (
            <PartnerCard key={p.name} {...p} />
          ))}
        </div>
      </div>

      <div className="partners-v2-group">
        <h3 className="partners-v2-group-title">راهبری و صدور گواهی</h3>
        <div className="partners-v2-grid">
          {PARTNERS_GOVERNANCE.map((p) => (
            <PartnerCard key={p.name} {...p} />
          ))}
        </div>
      </div>

      <div className="partners-v2-group">
        <h3 className="partners-v2-group-title">چارچوب‌های قانونی و اعتبار</h3>
        <div className="partners-v2-grid">
          {PARTNERS_FRAMEWORKS.map((p) => (
            <PartnerCard key={p.name} {...p} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PartnerCard = ({ mark, name, sub, accent }: PartnerEntry) => (
  <article className={`partner-v2-card accent-${accent}`}>
    <div className="partner-mark" aria-hidden="true">
      <span>{mark}</span>
    </div>
    <div className="partner-info">
      <div className="partner-name">{name}</div>
      <div className="partner-sub">{sub}</div>
    </div>
  </article>
);

const TestiV2Section = () => (
  <section className="testi-v2-section" data-reveal>
    <div className="shell">
      <div className="section-head">
        <div>
          <div className="section-eyebrow">سناریوهای کاربری</div>
          <h2 className="section-title">
            <span className="em">یک روز کاری</span> با سامانه.
          </h2>
        </div>
        <p className="section-lead">
          سه سناریوی نمایشی از سه نقش اصلی سامانه — فراگیر، رابط آموزش شرکت و کارشناس در مسیر
          شایستگی. این‌ها نمونهٔ طراحی محصول‌اند، نه نقل‌قول واقعی از کاربران.
        </p>
      </div>
      <div className="testi-v2-grid">
        {TESTI_V2.map((t) => (
          <article key={t.name} className={`testi-v2-card accent-${t.accent}`}>
            <div className="testi-card-top">
              <div className="quote-mark" aria-hidden="true">&ldquo;</div>
              <div className="testi-card-grad" aria-hidden="true"><GradCapSvg /></div>
            </div>
            <p className="quote-body">{t.body}</p>
            <div className="person">
              <div className="person-avatar" aria-hidden="true">
                {t.photo ? (
                  <img
                    src={t.photo}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    width="56"
                    height="56"
                  />
                ) : (
                  <span>{t.initials}</span>
                )}
              </div>
              <div className="person-info">
                <span className="person-name">{t.name}</span>
                <span className="person-role">{t.role}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Feature = ({ title, desc, icon }) => (
  <div>
    <div className="flex items-center gap-2.5 mb-2"  style={{ color: "var(--cyan)"}}>
      <Icon name={icon} size={18} />
      <div style={{ fontWeight: 600, color: "var(--fg)", fontSize: 14 }}>{title}</div>
    </div>
    <div style={{ fontSize: 13, color: "var(--fg-mute)", lineHeight: 1.6 }}>{desc}</div>
  </div>
);

const TechRow = ({ label, items }) => (
  <div className="grid gap-4.5"  style={{ gridTemplateColumns: "100px 1fr", padding: "12px 0", borderTop: "1px solid var(--line)"}}>
    <div className="uppercase"  style={{fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--fg-mute)", letterSpacing: "0.1em"}}>{label}</div>
    <div className="flex flex-wrap gap-1.5" >
      {items.map((i) => <span className="rounded-md" key={i}  style={{fontFamily: "var(--f-mono)", fontSize: 12, padding: "3px 9px", background: "var(--surface-2)", border: "1px solid var(--line)"}}>{i}</span>)}
    </div>
  </div>
);

// Mastery ring for pillar 3
const MasteryRing = ({ percent }) => {
  const r = 36, c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  return (
    <div className="flex items-center gap-4.5" >
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" stroke="var(--surface-3)" strokeWidth="6" />
        <circle cx="45" cy="45" r={r} fill="none" stroke="var(--violet)" strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform="rotate(-90 45 45)" />
        <text x="45" y="50" textAnchor="middle" fill="var(--fg)" fontFamily="var(--f-mono)" fontWeight="700" fontSize="18">{toFa(percent)}٪</text>
      </svg>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>تسلط فعلی</div>
        <div className="mt-1"  style={{fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--fg-mute)"}}>۷ از ۹ هدف</div>
      </div>
    </div>
  );
};

// ====== 3D hero floating cards ======
const Hero3DClassroom = () => (
  <div className="hero-card hc-classroom">
    <div className="head">
      <div className="title">کلاس زنده · حفاظت شبکه</div>
      <div className="meta">۴۲ حاضر</div>
    </div>
    <div className="mini-video">
      <span className="live-pill"><span className="dot"></span>LIVE</span>
      <span className="label">instructor · sharing</span>
    </div>
    <div className="mini-faces">
      {[0,1,2,3,4,5,6,7,8,9,10,11].map((i) => <div key={i} className="mini-face" />)}
    </div>
  </div>
);

const Hero3DTutor = () => (
  <div className="hero-card hc-tutor">
    <div className="head">
      <div className="title">AI Tutor</div>
      <div className="meta">RAG ready</div>
    </div>
    <div className="tutor-bubble user">
      <div className="av">من</div>
      <div>چرا ولتاژ انتهای فیدر افت می‌کند؟</div>
    </div>
    <div className="tutor-bubble">
      <div className="av">AI</div>
      <div>به امپدانس مسیر و بار فیدر فکر کن. در شبیه‌سازی پخش بار جلسه‌ی ۵، دقیقه ۲۲:۱۴ همین را دیدیم...</div>
    </div>
  </div>
);

const Hero3DAnalytics = () => (
  <div className="hero-card hc-analytics">
    <div className="head">
      <div className="title">سلامت کلاس</div>
      <div className="meta">real-time</div>
    </div>
    <div className="bar-row"><div className="bar-label">مشارکت</div><div className="bar-track"><div className="bar-fill" style={{width:"82%"}}/></div><div className="bar-val">۸۲</div></div>
    <div className="bar-row"><div className="bar-label">فهم</div><div className="bar-track"><div className="bar-fill amber" style={{width:"64%"}}/></div><div className="bar-val">۶۴</div></div>
    <div className="bar-row"><div className="bar-label">سرعت</div><div className="bar-track"><div className="bar-fill rose" style={{width:"94%"}}/></div><div className="bar-val">!۹۴</div></div>
    <div className="bar-row"><div className="bar-label">ابهام</div><div className="bar-track"><div className="bar-fill amber" style={{width:"38%"}}/></div><div className="bar-val">۳۸</div></div>
  </div>
);

const Hero3DCredential = () => (
  <div className="hero-card hc-credential">
    <div className="head">
      <div className="title">گواهی دیجیتال</div>
      <div className="meta">verifiable</div>
    </div>
    <div className="credential-mini">
      <div className="credential-seal float"></div>
      <div className="mt-3"  style={{fontSize: 12, color: "var(--fg-mute)"}}>گواهی نوع دوم:</div>
      <div className="mt-1"  style={{fontSize: 13, fontWeight: 600}}>بهره‌برداری شبکهٔ توزیع</div>
      <div className="mt-2"  style={{fontFamily: "var(--f-mono)", fontSize: 10, color: "var(--fg-dim)"}}>#7f3a · ۲۴ ساعت</div>
    </div>
  </div>
);

// ====== Phase-16 R9 — Trust strip (§2) ======
//
// Monogram tiles rendered inline so we don't pay an extra HTTP
// round-trip per logo. NRI rebrand: the tiles are the five real
// وزارت نیرو holdings the platform serves. (Component currently
// unrendered — kept for potential reuse, see D54 note above.)
const TRUST_PARTNERS = [
  { id: "tavanir", name: "توانیر", glyph: "ت" },
  { id: "tpph", name: "برق حرارتی", glyph: "ح" },
  { id: "wrm", name: "منابع آب", glyph: "آ" },
  { id: "abfa", name: "آبفا", glyph: "ف" },
  { id: "satkab", name: "ساتکاب", glyph: "س" },
];

const TrustStrip = () => (
  <section className="trust-strip" aria-label="هلدینگ‌های تحت پوشش">
    <div className="shell">
      <div className="trust-strip-eyebrow">
        <span className="dot"></span>
        <span>هلدینگ‌های تحت پوشش وزارت نیرو</span>
      </div>
      <ul className="trust-strip-row">
        {TRUST_PARTNERS.map((p) => (
          <li key={p.id} className="trust-strip-item" title={p.name}>
            <span className="trust-strip-glyph" aria-hidden="true">
              {p.glyph}
            </span>
            <span className="trust-strip-name">{p.name}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ====== Phase-16 R9 — Animated stats band (§3) ======
//
// Four numbers count up when the band scrolls into view. We honour
// `prefers-reduced-motion: reduce` by skipping the animation and
// rendering the final value immediately.
// NRI rebrand — the four numbers are the real وزارت نیرو coverage figures
// (same source as BRANDING.stats؛ «دامنهٔ پوشش هدف»، نه کاربر فعال فعلی).
const STATS_BAND = [
  { value: BRANDING.stats.faculties, suffix: "", label: "هلدینگ وزارت نیرو" },
  { value: BRANDING.stats.programs, suffix: "", label: "شرکت زیرمجموعه" },
  { value: BRANDING.stats.instructors, suffix: "", label: "نوع گواهی رسمی" },
  { value: BRANDING.stats.students, suffix: "", label: "کارمند صنعت — پوشش هدف" },
];

// Persian numeral conversion — share with the existing `toFa` helper
// in `shared.tsx` once we type that file, for now inline.
const toFaDigits = (n: number) =>
  String(n).replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const StatsBand = () => {
  const ref = React.useRef(null);
  const [displayed, setDisplayed] = React.useState(() => STATS_BAND.map(() => 0));
  const [hasRun, setHasRun] = React.useState(false);

  React.useEffect(() => {
    if (hasRun) return;
    const el = ref.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplayed(STATS_BAND.map((s) => s.value));
      setHasRun(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible || hasRun) return;
        setHasRun(true);
        const start = performance.now();
        const DURATION = 1400; // ms
        const tick = (now) => {
          const t = Math.min(1, (now - start) / DURATION);
          // ease-out cubic for a friendly count-up
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplayed(STATS_BAND.map((s) => Math.round(s.value * eased)));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasRun]);

  return (
    <section className="stats-band" ref={ref} aria-label="اعداد کلیدی صنعت">
      <div className="shell">
        <div className="stats-band-grid">
          {STATS_BAND.map((s, i) => (
            <div className="stats-band-cell" key={s.label}>
              <div className="stats-band-value">
                {toFaDigits(displayed[i])}
                {s.suffix}
              </div>
              <div className="stats-band-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ====== Phase-16 R10 §4 — Faculty showcase ======
//
// 6 instructor cards in a responsive grid. Each card reuses the
// existing 3D treatment (paper shadow + accent border on hover). The
// data is hardcoded here for now — TODO(phase-17) pull from
// /v1/users?role=instructor.
// NRI rebrand — نمونهٔ حوزه‌های تدریس (اسامی نمایشی، بدون ادعای وابستگی به
// نهاد واقعی). Component currently unrendered؛ kept for potential reuse.
const FACULTY_SHOWCASE = [
  {
    initials: "د. ع",
    name: "دکتر سپیده عظیمی",
    affiliation: "حوزهٔ برق",
    bio: "حفاظت و کنترل شبکه · ۱۸ سال سابقه تدریس",
  },
  {
    initials: "م. ک",
    name: "مهندس آرش کیانی",
    affiliation: "حوزهٔ انتقال و توزیع",
    bio: "بهره‌برداری شبکه · ۱۲ سال صنعت",
  },
  {
    initials: "د. ر",
    name: "دکتر مهرنوش رضوی",
    affiliation: "حوزهٔ آب",
    bio: "هیدرولوژی و مدیریت منابع آب",
  },
  {
    initials: "د. م",
    name: "دکتر بابک موسوی",
    affiliation: "حوزهٔ نیروگاهی",
    bio: "بهره‌برداری نیروگاه حرارتی",
  },
  {
    initials: "د. ط",
    name: "دکتر فاطمه طاهری",
    affiliation: "حوزهٔ HSE",
    bio: "ایمنی و محیط‌زیست تأسیسات",
  },
  {
    initials: "د. ف",
    name: "دکتر بهنام فرهادی",
    affiliation: "حوزهٔ انرژی",
    bio: "مدیریت انرژی و کاهش تلفات · ۲۰ سال پژوهش",
  },
];

const FacultyShowcase = ({ go }) => (
  <section className="section faculty-showcase" aria-label="مدرسان">
    <div className="shell">
      <div className="section-head reveal">
        <div className="text">
          <span className="eyebrow">FACULTY</span>
          <h2 className="h-1">مدرسانی از <span style={{ color: "var(--accent)" }}>دل صنعت آب و برق</span></h2>
        </div>
        <p className="lead">
          مدرسان پژوهشگاه نیرو در کنار خبرگان شاغل صنعت — از نیروگاه و شبکهٔ انتقال تا تصفیه‌خانه و شبکهٔ توزیع.
        </p>
      </div>
      <div className="faculty-grid">
        {FACULTY_SHOWCASE.map((f) => (
          <article key={f.name} className="faculty-card reveal">
            <div className="faculty-avatar" aria-hidden="true">{f.initials}</div>
            <h3 className="faculty-name">{f.name}</h3>
            <span className="faculty-affiliation">{f.affiliation}</span>
            <p className="faculty-bio">{f.bio}</p>
          </article>
        ))}
      </div>
      <div className="faculty-cta">
        <Button variant="outline" onClick={() => go("programs")}>
          مشاهده همه‌ی مدرسان
          <Icon name="arrow" size={14} />
        </Button>
      </div>
    </div>
  </section>
);

// ====== Phase-16 R10 §5 — Catalog teaser ======
//
// 6 featured courses in a responsive grid. On `<sm` they switch to a
// horizontal scroll-snap carousel so the user sees one course at a
// time without crammed half-cards. Container query inside the grid
// item makes the inner layout adapt to the slot, not just the
// viewport (so a course card in a sidebar elsewhere stacks
// vertically instead of overflowing).
const CATALOG_TEASER = [
  { code: "PWR-210", title: "بهره‌برداری شبکهٔ توزیع برق", by: "گروه آموزشی انتقال و توزیع", desc: "مانور شبکه · کاهش خاموشی.", weeks: "۸", glyph: "PWR" },
  { code: "WTR-310", title: "هیدرولیک شبکه‌های آبرسانی", by: "گروه آموزشی مهندسی آب", desc: "افت فشار · پمپاژ · شبیه‌سازی.", weeks: "۱۰", glyph: "WTR" },
  { code: "PWR-420", title: "حفاظت و کنترل سیستم قدرت", by: "گروه آموزشی برق", desc: "هماهنگی رله‌ها · پخش بار.", weeks: "۱۲", glyph: "REL" },
  { code: "WTR-440", title: "هیدرولوژی و منابع آب", by: "گروه آموزشی منابع آب", desc: "مدل‌سازی رواناب · تخصیص منابع.", weeks: "۱۰", glyph: "HYD" },
  { code: "HSE-110", title: "ایمنی و HSE تأسیسات", by: "گروه آموزشی ایمنی", desc: "الزامی نقش‌محور بهره‌برداری.", weeks: "۶", glyph: "HSE" },
  { code: "ENR-350", title: "مدیریت انرژی و کاهش تلفات", by: "گروه آموزشی برنامه‌ریزی انرژی", desc: "ممیزی انرژی · تحلیل تلفات.", weeks: "۸", glyph: "ENR" },
];

const CatalogTeaser = ({ go }) => (
  <section className="section catalog-teaser" aria-label="دوره‌های برگزیده">
    <div className="shell">
      <div className="section-head reveal">
        <div className="text">
          <span className="eyebrow">FEATURED COURSES</span>
          <h2 className="h-1">دوره‌های <span style={{ color: "var(--navy)" }}>منتخب صنعت</span></h2>
        </div>
        <p className="lead">
          شش دورهٔ نمونه از کاتالوگ ضمن خدمت — همه با گواهی ساعت‌محور نوع اول/دوم و قابل‌راستی‌آزمایی آنلاین.
        </p>
      </div>
      <div className="catalog-teaser-rail">
        {CATALOG_TEASER.map((c) => (
          <article key={c.code} className="catalog-teaser-card reveal">
            <div className={"catalog-teaser-glyph"}>{c.glyph}</div>
            <div className="catalog-teaser-body">
              <div className="catalog-teaser-code">{c.code}</div>
              <h3 className="catalog-teaser-title">{c.title}</h3>
              <p className="catalog-teaser-by">{c.by}</p>
              <p className="catalog-teaser-desc">{c.desc}</p>
              <div className="catalog-teaser-meta">
                <span>{c.weeks} هفته</span>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="catalog-teaser-cta">
        <Button variant="outline" onClick={() => go("catalog")}>
          مشاهده‌ی کاتالوگ دوره‌ها
          <Icon name="arrow" size={14} />
        </Button>
      </div>
    </div>
  </section>
);

// ====== Phase-16 R10 §6 — usage scenarios + dual CTA ======
// NRI rebrand (بدون testimonial جعلی): explicitly-labelled sample scenarios
// for the in-service personas. Component currently unrendered؛ kept for reuse.
const TESTIMONIALS = [
  {
    initials: "ب",
    name: "کارشناس بهره‌برداری برق",
    role: "سناریوی نمایشی · هلدینگ توانیر",
    quote:
      "دوره‌ی الزامی حفاظت شبکه رو با کلاس زنده گذروندم، همون سناریو رو در شبیه‌ساز پخش بار تمرین کردم و ساعت‌هام خودکار در شناسنامهٔ آموزشی ثبت شد.",
  },
  {
    initials: "ر",
    name: "رابط آموزش شرکت",
    role: "سناریوی نمایشی · هلدینگ آبفا",
    quote:
      "کارکنان رو برای گروه‌های تقویم آموزشی نامزد می‌کنم و پرداخت دوره‌های الزامی از کیف پول سازمانی انجام می‌شه — سقف ساعت هر نفر هم جلوی چشمه.",
  },
  {
    initials: "م",
    name: "کارشناس منابع آب",
    role: "سناریوی نمایشی · مدیریت منابع آب ایران",
    quote:
      "پکیج اختیاری هیدرولوژی رو از بازار گرفتم؛ گواهی نوع دومم رو کارفرما با لینک راستی‌آزمایی آنلاین تأیید کرد.",
  },
];

const Testimonials = ({ go }) => (
  <section className="section testimonials" aria-label="سناریوهای کاربری">
    <div className="shell">
      <div className="section-head reveal">
        <div className="text">
          <span className="eyebrow">SCENARIOS</span>
          <h2 className="h-1">سه روایتِ <span style={{ color: "var(--sage)" }}>نمایشی</span> از سه نقش</h2>
        </div>
        <p className="lead">
          فراگیر، رابط آموزش شرکت و کارشناس در مسیر شایستگی — نمونهٔ طراحی محصول، نه نقل‌قول واقعی.
        </p>
      </div>
      <div className="testimonials-grid">
        {TESTIMONIALS.map((t) => (
          <article key={t.name} className="testimonial-card reveal">
            <svg
              className="testimonial-mark"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              aria-hidden="true"
              fill="currentColor"
            >
              <path d="M9 8c-3 1-5 4-5 8v8h8v-9H7c0-2 1-4 3-5l-1-2zm14 0c-3 1-5 4-5 8v8h8v-9h-5c0-2 1-4 3-5l-1-2z" />
            </svg>
            <p className="testimonial-quote">«{t.quote}»</p>
            <footer className="testimonial-footer">
              <span className="testimonial-avatar" aria-hidden="true">{t.initials}</span>
              <span>
                <strong>{t.name}</strong>
                <br />
                <span>{t.role}</span>
              </span>
            </footer>
          </article>
        ))}
      </div>
      <div className="testimonials-cta-row">
        <Button variant="primary" size="lg" onClick={() => go("apply")}
        >
          {BRANDING.copy.primaryCta}
          <Icon name="arrow" size={16} />
        </Button>
        <Button variant="outline" size="lg" onClick={() => go("programs")}
        >
          مشاهده دوره‌ها
          <Icon name="grad" size={16} />
        </Button>
      </div>
    </div>
  </section>
);

// ====== Course catalog data (نمونه دوره‌های ضمن خدمت صنعت آب و برق) ======
// «ساعت گواهی» = طراحی آموزشی دوره (ساعت‌محور، نوع اول/دوم) — نه آمار کاربر.
const COURSES = [
  { title: "بهره‌برداری و نگهداری شبکهٔ توزیع برق", code: "PWR-210", by: "گروه آموزشی انتقال و توزیع", desc: "از مانور شبکه تا کاهش خاموشی، با سناریوهای شبیه‌سازی‌شده.", weeks: "۸", modules: "۱۶", hours: "۲۴", cover: "cyan", glyph: "PWR" },
  { title: "هیدرولیک شبکه‌های آبرسانی", code: "WTR-310", by: "گروه آموزشی مهندسی آب", desc: "افت فشار، پمپاژ و طراحی شبکه — با شبیه‌سازی هیدرولیک.", weeks: "۱۰", modules: "۲۰", hours: "۳۲", cover: "amber", glyph: "WTR" },
  { title: "حفاظت و کنترل سیستم‌های قدرت", code: "PWR-420", by: "گروه آموزشی برق", desc: "هماهنگی رله‌ها و پایداری ولتاژ، با تمرین پخش بار.", weeks: "۱۲", modules: "۲۴", hours: "۴۰", cover: "violet", glyph: "REL" },
  { title: "هیدرولوژی و مدیریت منابع آب", code: "WTR-440", by: "گروه آموزشی منابع آب", desc: "مدل‌سازی رواناب، تخصیص منابع و مدیریت خشکسالی.", weeks: "۱۰", modules: "۲۰", hours: "۳۲", cover: "deep", glyph: "HYD" },
  { title: "ایمنی و HSE در تأسیسات آب و برق", code: "HSE-110", by: "گروه آموزشی ایمنی", desc: "الزامی نقش‌محور برای مشاغل بهره‌برداری و میدانی.", weeks: "۶", modules: "۱۲", hours: "۱۶", cover: "rose", glyph: "HSE" },
  { title: "مدیریت انرژی و کاهش تلفات", code: "ENR-350", by: "گروه آموزشی برنامه‌ریزی انرژی", desc: "ممیزی انرژی و تحلیل تلفات فیدر با دادهٔ واقعی شبکه.", weeks: "۸", modules: "۱۶", hours: "۲۴", cover: "green", glyph: "ENR" },
];

export default HomePage;
